// plan-oracle S02: streaming client for the vague oracle — strict-TS port of
// handoff/reference/oracle-client.js. Stateless: sends only the user's question;
// the SYSTEM prompt + sampling live in the Ollama Modelfile (deployed server-side).
// POSTs to the same-origin /oracle/stream (nginx in prod, the vite proxy in dev)
// and reads Ollama's newline-delimited JSON stream.
//
// Three things this client MUST do for the Qwen3-4B oracle:
//   1. Send  think: false  — Qwen3 is a hybrid-thinking model. Thinking OFF end-to-end.
//   2. Defensively STRIP any <think>...</think> from message.content and IGNORE
//      message.thinking (belt-and-suspenders behind think:false + the Modelfile's
//      closed-<think> prefill).
//   3. Own its lifetime: an AbortController with a timeout that the widget's
//      dismiss() signal also aborts, so a cold ~2.5 GB load or a hung stream never
//      strands the UI — the widget falls back to commune.

export class OracleError extends Error {
  status: number
  constructor(status: number) {
    super(`oracle unreachable: ${status}`)
    this.status = status
    this.name = 'OracleError'
  }
}

// Generous overall cap: >= a cold 4B load, <= the nginx proxy_read_timeout (300 s).
// A progressing stream resets nginx's per-read timer per token, so this mainly guards
// a hung / never-first-token summon. The widget's dismiss() aborts via `signal`.
export const ORACLE_TIMEOUT_MS = 180_000

interface SummonOpts {
  onToken?: (piece: string, full: string) => void
  signal?: AbortSignal
  timeoutMs?: number
}

interface ChatChunk {
  message?: { content?: string }
  done?: boolean
}

// Strip Qwen3 reasoning that should never reach the terminal. Handles a closed
// <think>...</think> block, a dangling unclosed <think>, and stray tags.
function stripThink(s: string): string {
  return s
    .replace(/<think>[\s\S]*?<\/think>/gi, '')
    .replace(/<think>[\s\S]*$/gi, '')
    .replace(/<\/?think>/gi, '')
}

/**
 * Summon the oracle. Returns the full answer (thinking stripped).
 * Throws `OracleError(status)` on a non-2xx / empty body; aborts on timeout or
 * on the passed `signal` (the widget distinguishes a user-dismiss from a failure).
 */
export async function summon(
  question: string,
  { onToken, signal, timeoutMs = ORACLE_TIMEOUT_MS }: SummonOpts = {},
): Promise<string> {
  // Internal controller so we can enforce a timeout AND honor the widget's signal.
  const ctrl = new AbortController()
  const onAbort = (): void => ctrl.abort()
  if (signal) {
    if (signal.aborted) ctrl.abort()
    else signal.addEventListener('abort', onAbort, { once: true })
  }
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)

  try {
    const res = await fetch('/oracle/stream', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'vpk-oracle',
        messages: [{ role: 'user', content: question }],
        stream: true,
        think: false, // Qwen3 thinking OFF — no <think> in the stream.
      }),
      signal: ctrl.signal,
    })
    // 429 → busy (consulting others); 5xx / throw / timeout-abort → widget falls
    // back to commune. dismiss() (via `signal`) aborts the fetch — clean teardown.
    if (!res.ok || !res.body) throw new OracleError(res.status)

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buf = ''
    let full = ''
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      let nl: number
      while ((nl = buf.indexOf('\n')) >= 0) {
        const line = buf.slice(0, nl).trim()
        buf = buf.slice(nl + 1)
        if (!line) continue
        let obj: ChatChunk
        try {
          obj = JSON.parse(line) as ChatChunk
        } catch {
          continue
        }
        // IGNORE message.thinking entirely; STRIP any <think> that leaked into content.
        const piece = stripThink(obj.message?.content ?? '')
        if (piece) {
          full += piece
          onToken?.(piece, full)
        }
        if (obj.done) return full
      }
    }
    return full
  } finally {
    clearTimeout(timer)
    if (signal) signal.removeEventListener('abort', onAbort)
  }
}
